import { Range, getTrackBackground } from "react-range";

import { InfoCircleFilled } from "@ant-design/icons";
import { Tooltip } from "antd";
import { selectLabelDensity, selectMapState, useMapStore } from "stores/map";
import styled from "styled-components";
import { getFromRecord } from "utils/fp";

import { LABEL_DENSITY_MIN_ZOOM } from "components/map/hooks/useLabelDensity";

const MIN_DISPLAY_PERCENT = 25;
const DISPLAY_RANGE = 75; // Max (100) - Min (25)

function toDisplayPercent(density: number): number {
  return Math.round(density * DISPLAY_RANGE + MIN_DISPLAY_PERCENT);
}

export function LabelDensitySlider({ layerName }: { layerName: string }) {
  const setLabelDensity = useMapStore((s) => s.setLabelDensity);
  const densityMap = useMapStore(selectLabelDensity);
  const density = getFromRecord(0)(densityMap, layerName);
  const mapState = useMapStore(selectMapState);
  const zoom = mapState.zoom;
  const isDisabled = zoom <= LABEL_DENSITY_MIN_ZOOM;
  const effectiveDensity = isDisabled ? 0 : density;
  const displayPercent = toDisplayPercent(effectiveDensity);

  return (
    <>
      <LabelWrapper>
        <Tooltip
          title={
            isDisabled
              ? `Label density is available at zoom levels above ${LABEL_DENSITY_MIN_ZOOM}. Zoom in to enable.`
              : "Adjust how many labels appear on the map. 25% shows fewer labels to reduce clutter. 100% shows every label."
          }>
          <label>
            Label Density
            <InfoCircleFilled
              style={{ fontSize: "1rem", marginLeft: 4, opacity: 0.4 }}
            />{" "}
            <SubText>{`Current: ${displayPercent}%`}</SubText>
          </label>
        </Tooltip>
      </LabelWrapper>

      <RangeWrapper>
        <Range
          step={0.05}
          min={0}
          max={1}
          disabled={isDisabled}
          values={[effectiveDensity]}
          onChange={([value]: number[]) => setLabelDensity(layerName, value)}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              style={{
                ...props.style,
                background: getTrackBackground({
                  values: [effectiveDensity],
                  colors: ["#00c7b1", "#a2aaad"],
                  min: 0,
                  max: 1
                }),
                opacity: isDisabled ? 0.4 : 1
              }}
              className="range-track">
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <Tooltip
              placement="bottom"
              open={effectiveDensity === 0 ? undefined : false}
              title="Minimum possible density is 25%">
              <div
                {...props}
                style={{
                  ...props.style
                }}
                className="range-thumb">
                <div className="range-label">
                  {toDisplayPercent(effectiveDensity)}
                  <PercentSign>%</PercentSign>
                </div>
              </div>
            </Tooltip>
          )}
        />
      </RangeWrapper>
    </>
  );
}

const SubText = styled.span`
  display: block;
  font-size: 0.8em;
  line-height: 0.8em;
  color: #a2aaad;
`;

const LabelWrapper = styled.div`
  width: 100%;
`;

const PercentSign = styled.span`
  font-size: 0.75em;
  opacity: 0.5;
`;

const RangeWrapper = styled.div`
  width: 100%;
  padding: 28px 8px 0;

  .range-track {
    height: 4px;
    width: 100%;
    background-color: #a2aaad;
    cursor: pointer;
  }

  .range-thumb {
    height: 10px;
    width: 10px;
    background-color: var(--color-white);
    border-radius: 50%;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.44);
    outline: none;
    cursor: grab;
  }

  .range-label {
    position: absolute;
    top: -28px;
    left: 50%;
    transform: translateX(-50%);
    color: var(--color-text);
    font-weight: var(--fontWeightMedium);
    font-size: 1.4rem;
    white-space: nowrap;
  }
`;
